import { httpsCallable } from 'firebase/functions'
import { collection, query, orderBy, getDocs, where, limit, updateDoc, arrayUnion, addDoc, serverTimestamp } from 'firebase/firestore'
import { functions, db, auth } from '../firebase'

class FirebaseChatService {
  constructor() {
    this.userService = null
  }

  async retrievePreviousMessages() {
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated')
      }

      const q = query(
        collection(db, 'chats'),
        where('userId', '==', auth.currentUser.uid),
        orderBy('createdAt', 'desc')
      )

      const snapshot = await getDocs(q)

      const messages = snapshot.docs.flatMap(doc => {
        const data = doc.data()
        if (!data.messages || !Array.isArray(data.messages)) return []

        return data.messages.map(msg => ({
          id: msg.id || Date.now().toString(),
          role: msg.role || 'assistant',
          content: msg.content || '',
          createdAt: msg.createdAt?.toDate() || new Date()
        }))
      })

      const filteredMessages = messages.filter(msg => msg.role !== 'system');
      
      return filteredMessages
    } catch (error) {
      console.error('Error fetching previous messages:', error)
      return []
    }
  }

  async sendMessage(message) {
    try {
      if (!auth.currentUser) {
        throw new Error('User not authenticated')
      }

      const chatFunction = httpsCallable(functions, 'chat')
      const result = await chatFunction({ message })
      
      if (result.data) {
        return {
          id: Date.now().toString(),
          role: 'assistant',
          content: Array.isArray(result.data.response) ? result.data.response.join('') : result.data.response,
          createdAt: new Date()
        }
      } else {
        throw new Error('Unexpected response format')
      }
    } catch (error) {
      console.error('Firebase Functions Error:', error)
      if (error.code) {
        throw new Error(`Failed to get chat response: ${error.message}`)
      }
      throw error
    }
  }

  async getLastMessage(userId) {
    try {
      const chatRef = collection(db, 'chats')
      const q = query(
        chatRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(1)
      )
      const querySnapshot = await getDocs(q)
      if (querySnapshot.empty) {
        return null
      }
      const lastChatDoc = querySnapshot.docs[0].data()
      if (lastChatDoc.messages && lastChatDoc.messages.length > 0) {
        // Assuming messages within a document are also sorted, taking the last one.
        const lastMessage = lastChatDoc.messages[lastChatDoc.messages.length - 1]
        return lastMessage.createdAt
      }
      return lastChatDoc.createdAt // Fallback to document's createdAt
    } catch (error) {
      console.error(`Error fetching last message for user ${userId}:`, error)
      return null
    }
  }

  async getUserQuestionCount(userId) {
    try {
      const chatRef = collection(db, 'chats');
      const q = query(chatRef, where('userId', '==', userId));
      const querySnapshot = await getDocs(q);
      
      let questionCount = 0;
      querySnapshot.forEach(doc => {
        const data = doc.data();
        if (data.messages && Array.isArray(data.messages)) {
          questionCount += data.messages.filter(msg => msg.role === 'user').length;
        }
      });
      return questionCount;
    } catch (error) {
      console.error(`Error fetching question count for user ${userId}:`, error);
      return 0;
    }
  }

  async getUserChatHistory(userId, includeSystemMessages = false) {
    try {
      const chatRef = collection(db, 'chats')
      const q = query(
        chatRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc')
      )
      const querySnapshot = await getDocs(q)
      const allMessages = []
      
      querySnapshot.forEach(doc => {
        const data = doc.data()
        if (data.messages && Array.isArray(data.messages)) {
          allMessages.push(...data.messages.map(msg => ({
            content: msg.content,
            role: msg.role,
            createdAt: msg.createdAt,
            isUserChartUpdated: msg.isUserChartUpdated || false,
            feedbackThumb: msg.feedbackThumb || null,
            originalContent: msg.originalContent || null,
            messageId: msg.messageId || null,
            toolsUsed: msg.toolsUsed || null,
            suggestedQuestions: msg.suggestedQuestions || null,
            isReengagement: msg.isReengagement || false
          })))
        }
        if (data.promptVersionUpdatedAt) {
          allMessages.push({
            role: 'prompt_update',
            content: `Prompt updated to v${data.promptVersion || '?'}`,
            createdAt: data.promptVersionUpdatedAt,
            isPromptUpdate: true,
            promptVersion: data.promptVersion
          })
        }
      })
      
      if (includeSystemMessages) {
        return allMessages
      }
      
      return allMessages.filter(msg => msg.role !== 'system');
    } catch (error) {
      console.error('Error fetching chat history:', error)
      throw error
    }
  }

  async addAssistantMessage(userId, messageContent) {
    if (!userId || !messageContent) {
      throw new Error('User ID and message content are required.')
    }

    const chatRef = collection(db, 'chats')
    const q = query(
      chatRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(1)
    )

    const querySnapshot = await getDocs(q)

    const newMessage = {
      role: 'assistant',
      content: messageContent,
      createdAt: new Date(),
      messageId: crypto.randomUUID()
    }

    if (querySnapshot.empty) {
      await addDoc(chatRef, {
        userId: userId,
        createdAt: serverTimestamp(),
        messages: [newMessage]
      })
    } else {
      const latestChatDocRef = querySnapshot.docs[0].ref
      await updateDoc(latestChatDocRef, {
        messages: arrayUnion(newMessage)
      })
    }
  }

  async sendPushNotification(userId, title, message) {
    if (!userId || !title || !message) {
      throw new Error('User ID, title, and message are required for push notifications.')
    }
    
    try {
      const pushNotificationFunction = httpsCallable(functions, 'pushNotification')
      await pushNotificationFunction({ firebaseId: userId, title, message })
    } catch (error) {
      console.error('Error sending push notification:', error)
      throw error
    }
  }

  async replaceUserChatHistory(userId, newMessages) {
    if (!userId || !Array.isArray(newMessages)) {
      throw new Error('User ID and messages array are required.')
    }

    try {
      const chatRef = collection(db, 'chats')
      const q = query(
        chatRef,
        where('userId', '==', userId),
        orderBy('createdAt', 'desc'),
        limit(1)
      )

      const querySnapshot = await getDocs(q)

      if (querySnapshot.empty) {
        await addDoc(chatRef, {
          userId: userId,
          createdAt: serverTimestamp(),
          messages: newMessages,
          updatedAt: serverTimestamp(),
          promptVersion: 1
        })
      } else {
        const latestChatDocRef = querySnapshot.docs[0].ref
        
        await updateDoc(latestChatDocRef, {
          messages: newMessages,
          updatedAt: serverTimestamp()
        })
      }
    } catch (error) {
      console.error('Error replacing chat history:', error)
      throw error
    }
  }

  async userHasChat(userId) {
    try {
      const chatRef = collection(db, 'chats')
      const q = query(chatRef, where('userId', '==', userId), limit(1))
      const snap = await getDocs(q)
      return !snap.empty
    } catch (error) {
      console.error(`Error checking chat for user ${userId}:`, error)
      return false
    }
  }

  async getUserChatCount(userId) {
    try {
      const chatRef = collection(db, 'chats')
      const q = query(chatRef, where('userId', '==', userId))
      const snap = await getDocs(q)
      let count = 0
      snap.forEach(doc => {
        const messages = doc.data().messages
        if (Array.isArray(messages)) {
          count += messages.filter(m => m.role === 'user').length
        }
      })
      return count
    } catch (error) {
      console.error(`Error getting chat count for user ${userId}:`, error)
      return 0
    }
  }

  setUserService(userService) {
    this.userService = userService
  }
}

export { FirebaseChatService } 